import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar as CalendarIcon, 
  List, 
  DollarSign, 
  Receipt, 
  Package, 
  FileText, 
  User, 
  Vote,
  ChevronLeft, 
  ChevronRight, 
  LayoutList, 
  CalendarDays, 
  ChevronDown, 
  ChevronUp,
  Sparkles,
  X,
  Calendar,
  Star,
  Rocket,
  Zap,
  Bug,
  Gift,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import StatCard from '../common/StatCard';
import { formatCurrency, formatDate } from '../../utils/formatters';

// =====================================================
// CHANGELOG DATA - Add new updates at the TOP
// =====================================================
const CHANGELOG = [
  {
  version: '1.9.0',
  date: '2025-12-13',
  title: 'Documents & File Management',
  type: 'feature',
  highlights: [
    'New Documents page - store contracts, riders, stage plots & more',
    'Upload and organize files by category (contracts, riders, stage plots, receipts, promo)',
    'Link documents to specific shows',
    'View linked documents in Show Detail modal',
    'Search, filter, and favorite your documents',
    'Preview images and PDFs directly in the app',
    'Grid and list view options',
  ]
},
  {
  version: '1.8.0',
  date: '2025-12-09',
  title: 'Bands & Mobile Improvements',
  type: 'feature',
  highlights: [
    'Add bands/artists to shows - track who\'s playing each gig',
    'Bands display as tags in show details',
    'Fixed mobile layout for time inputs (Load In & Show Time)',
    'Improved mobile responsiveness in Show Detail modal',
  ]
},
  {
  version: '1.7.0',
  date: '2025-12-07',
  title: 'Security Hardening & Dashboard Customization',
  type: 'feature',
  highlights: [
    'Drag and drop to reorder navigation cards on dashboard',
    'Card order saves automatically and persists across sessions',
    'Reset button now restores both widget sections and card order',
  ]
},
  {
    version: '1.6.0',
    date: '2025-12-05',
    title: 'Sales Mode Show Integration',
    type: 'feature',
    highlights: [
      'Link merch sales to specific shows for detailed reporting',
      'Show picker appears when entering Sales Mode - select from shows 7 days past to 30 days ahead',
      'Real-time session tracking shows items sold and revenue in Sales Mode header',
      'Session summary modal displays breakdown before saving',
      'Sales automatically update show\'s Cash Merch field',
      'View merch sales breakdown in Show Detail modal',
      'Sales log stored per-user in Firebase for historical records',
      'Skip show selection to use Sales Mode without linking to a show'
    ]
  },
  {
    version: '1.5.0',
    date: '2025-12-04',
    title: 'Band Polls & Voting',
    type: 'feature',
    highlights: [
      '3 poll types: Yes/No, Show Offer, and Custom (2-6 options)',
      'Show Offer polls auto-fill venue, date, and guarantee from tentative shows',
      'See who voted after casting your vote',
      'Notification badges show unvoted polls count',
      'Auto-expire polls: 1 day, 3 days, 1 week, 2 weeks, or manual close',
      'Admin controls to close or delete polls',
      'Shared bottom navigation across all pages'
    ]
  },
  {
    version: '1.4.0',
    date: '2025-12-04',
    title: 'Documents Overhaul',
    type: 'feature',
    highlights: [
      'Added 6 document categories (Contracts, Riders, Stage Plots, Receipts, Promo, Other)',
      'Link documents to specific shows',
      'Add tags and notes to documents',
      'Favorites system for quick access',
      'Grid and list view toggle',
      'Search by name, tags, or notes',
      'Sort by date, name, or size'
    ]
  },
  {
    version: '1.3.0',
    date: '2025-12-03',
    title: 'Welcome Wizard & Feedback System',
    type: 'feature',
    highlights: [
      'New onboarding wizard for first-time users',
      'In-app feedback submission with screenshots',
      'Admin feedback dashboard',
      'Confetti celebration on setup completion'
    ]
  },
  {
    version: '1.2.0',
    date: '2025-12-01',
    title: 'Dashboard Customization',
    type: 'feature',
    highlights: [
      'Drag and drop to reorder dashboard widgets',
      'Hide/show widgets you don\'t need',
      'Your layout saves automatically',
      'Reset to default option'
    ]
  },
  {
    version: '1.1.0',
    date: '2025-11-28',
    title: 'Multi-Band Support',
    type: 'feature',
    highlights: [
      'Create and manage multiple bands',
      'Invite members via code or email',
      'Role-based permissions (Admin/Member)',
      'Real-time sync across all members'
    ]
  },
  {
    version: '1.0.0',
    date: '2025-11-15',
    title: 'Initial Release',
    type: 'feature',
    highlights: [
      'Show/event calendar with recurring events',
      'Financial tracking and summaries',
      'Inventory management with sizes',
      'Document storage',
      'Contact management',
      'Bandsintown import'
    ]
  }
];

const TYPE_CONFIG = {
  feature: { 
    color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400', 
    icon: Rocket,
    label: 'New Feature'
  },
  improvement: { 
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400', 
    icon: Zap,
    label: 'Improvement'
  },
  fix: { 
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400', 
    icon: Bug,
    label: 'Bug Fix'
  }
};

// =====================================================
// WHAT'S NEW WIDGET
// =====================================================
export const WhatsNewWidget = ({ setFeedbackModalOpen }) => {
  const [showModal, setShowModal] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const latestUpdate = CHANGELOG[0];

  const isNew = (dateStr) => {
    const diffDays = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  return (
    <>
      <div id="whatsnew" className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden h-full">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                <Sparkles className="text-purple-600 dark:text-purple-400" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-100">What's New</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Latest updates</p>
              </div>
            </div>
            {isNew(latestUpdate.date) && (
              <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">NEW</span>
            )}
          </div>
        </div>

        <div className="p-4">
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_CONFIG[latestUpdate.type].color}`}>
                {TYPE_CONFIG[latestUpdate.type].label}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">v{latestUpdate.version}</span>
            </div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">{latestUpdate.title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {new Date(latestUpdate.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          <ul className="space-y-1 mb-4">
            {latestUpdate.highlights.slice(0, 3).map((highlight, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Star size={12} className="text-yellow-500 mt-1 flex-shrink-0" />
                <span className="line-clamp-1">{highlight}</span>
              </li>
            ))}
            {latestUpdate.highlights.length > 3 && (
              <li className="text-sm text-purple-600 dark:text-purple-400 pl-5">+{latestUpdate.highlights.length - 3} more</li>
            )}
          </ul>

          <button
            onClick={() => setShowModal(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-xl font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors mb-4"
          >
            View All Updates
            <ChevronRight size={16} />
          </button>

          {/* Feedback Callout */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-3 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-2">
              <span className="text-xl">💡</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
                  Help shape Boply!
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  We're in beta and your feedback matters. Tell us what features you'd love to see or report any bugs you find.
                </p>
                <button
                  onClick={() => setFeedbackModalOpen && setFeedbackModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  <Gift size={14} />
                  Send Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                  <Sparkles className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">What's New in Boply</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Release notes & changelog</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(85vh-100px)] p-6">
              {/* Beta Feedback Banner */}
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-4 mb-6 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">🚀</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-1">Boply is in Beta!</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Thanks for being an early user! Your feedback directly influences what we build next. Found a bug? Have a feature idea? We want to hear it all.
                    </p>
                  </div>
                  <button
                    onClick={() => { setShowModal(false); if (setFeedbackModalOpen) setFeedbackModalOpen(true); }}
                    className="flex-shrink-0 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Gift size={16} />
                    Share Feedback
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {CHANGELOG.map((release, index) => {
                  const TypeIcon = TYPE_CONFIG[release.type].icon;
                  const isExpanded = expandedVersion === release.version;
                  return (
                    <div key={release.version} className={`relative pl-8 pb-6 ${index !== CHANGELOG.length - 1 ? 'border-l-2 border-gray-200 dark:border-gray-700' : ''}`}>
                      <div className={`absolute left-0 -translate-x-1/2 w-4 h-4 rounded-full ${index === 0 ? 'bg-purple-500 ring-4 ring-purple-100 dark:ring-purple-900/50' : 'bg-gray-300 dark:bg-gray-600'}`} />
                      <div className={`bg-gray-50 dark:bg-gray-900 rounded-xl p-4 cursor-pointer transition-all ${isExpanded ? 'ring-2 ring-purple-500' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`} onClick={() => setExpandedVersion(isExpanded ? null : release.version)}>
                        <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_CONFIG[release.type].color}`}>
                              <TypeIcon size={12} />{TYPE_CONFIG[release.type].label}
                            </span>
                            <span className="text-sm font-mono text-gray-500 dark:text-gray-400">v{release.version}</span>
                            {isNew(release.date) && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">NEW</span>}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(release.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{release.title}</h3>
                        <ul className={`space-y-1.5 overflow-hidden transition-all ${isExpanded ? 'max-h-[500px]' : 'max-h-[72px]'}`}>
                          {release.highlights.map((h, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                              <span className="text-green-500 mt-0.5">✓</span>{h}
                            </li>
                          ))}
                        </ul>
                        {release.highlights.length > 3 && !isExpanded && (
                          <p className="text-sm text-purple-600 dark:text-purple-400 mt-2 font-medium">Click to see more...</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom CTA */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="text-2xl">💬</span>
                  <span className="text-lg font-bold text-gray-800 dark:text-gray-100">We'd love to hear from you!</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
                  Whether it's a bug report, feature request, or just to say hi — your feedback helps us build a better tool for musicians everywhere.
                </p>
                <button 
                  onClick={() => { setShowModal(false); if (setFeedbackModalOpen) setFeedbackModalOpen(true); }} 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  <Gift size={18} />
                  Send Your Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// =====================================================
// STATS WIDGET
// =====================================================
export const StatsWidget = ({ summary, totalInventoryValue, upcomingShows, isAdmin = true }) => {
  const totalShowsCount = summary.byDay.reduce((count, day) => {
  const realShows = day.shows?.filter(s => 
    s.eventType !== 'other' && 
    s.status !== 'tentative'
  ) || [];
  return count + realShows.length;
}, 0);
  
  const pastShowsCount = summary.byDay.reduce((count, day) => {
  if (new Date(day.date) < new Date()) {
    const realShows = day.shows?.filter(s => 
      s.eventType !== 'other' && 
      s.status !== 'tentative'
    ) || [];
    return count + realShows.length;
  }
  return count;
}, 0);

  return (
    <div id="stats" className="bg-transparent">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {isAdmin ? (
          <StatCard 
            label="Net Profit" 
            value={formatCurrency(summary.netProfit)} 
            className="text-purple-600 dark:text-purple-400"
          />
        ) : (
          <StatCard 
            label="Your Shows" 
            value={totalShowsCount} 
            className="text-purple-600 dark:text-purple-400"
          />
        )}
        <StatCard 
          label="Total Shows" 
          value={totalShowsCount} 
          className="text-blue-600 dark:text-blue-400"
        />
        {isAdmin ? (
          <StatCard 
            label="Inventory Value" 
            value={formatCurrency(totalInventoryValue)} 
            className="text-indigo-600 dark:text-indigo-400"
          />
        ) : (
          <StatCard 
            label="Past Shows" 
            value={pastShowsCount} 
            className="text-indigo-600 dark:text-indigo-400"
          />
        )}
        <StatCard 
          label="Upcoming" 
          value={upcomingShows.filter(s => s.eventType !== 'other').length} 
          className="text-green-600 dark:text-green-400"
        />
      </div>
    </div>
  );
};

// =====================================================
// NAVIGATION WIDGET - With Draggable Cards
// =====================================================
const NAV_CARDS_CONFIG = {
  calendar: { id: 'calendar', icon: CalendarIcon, title: 'Calendar', description: 'View and manage your show schedule', color: 'gray', page: 'calendar', adminOnly: false },
  timeline: { id: 'timeline', icon: List, title: 'Timeline', description: 'Upcoming and past shows at a glance', color: 'purple', page: 'timeline', adminOnly: false },
  polls: { id: 'polls', icon: Vote, title: 'Polls', description: 'Vote on band decisions together', color: 'pink', page: 'polls', adminOnly: false },
  summary: { id: 'summary', icon: DollarSign, title: 'Summary', description: 'Income, expenses, and profit breakdown', color: 'green', page: 'summary', adminOnly: true },
  expenses: { id: 'expenses', icon: Receipt, title: 'Expenses', description: 'Track van, merch, and equipment costs', color: 'orange', page: 'expenses', adminOnly: true },
  inventory: { id: 'inventory', icon: Package, title: 'Inventory', description: 'Manage your merch stock and pricing', color: 'indigo', page: 'inventory', adminOnly: false },
  documents: { id: 'documents', icon: FileText, title: 'Documents', description: 'Store contracts, input lists, and stage plots', color: 'purple', page: 'documents', adminOnly: false },
  contacts: { id: 'contacts', icon: User, title: 'Contacts', description: 'Manage venue and promoter contacts', color: 'pink', page: 'contacts', adminOnly: true },
};

const DEFAULT_NAV_ORDER = ['calendar', 'timeline', 'polls', 'summary', 'expenses', 'inventory', 'documents', 'contacts'];

const navColorClasses = {
  gray: 'bg-gray-100 dark:bg-gray-700/30 text-gray-600 dark:text-gray-400',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
};

const SortableNavCard = ({ id, card, onNavigate, isEditing }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !isEditing });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 'auto' };
  const Icon = card.icon;

  return (
    <div ref={setNodeRef} style={style} className={`relative ${isDragging ? 'opacity-50 scale-105' : ''}`}>
      {isEditing && (
        <div {...attributes} {...listeners} className="absolute -top-2 -right-2 z-10 p-1.5 bg-purple-600 text-white rounded-lg cursor-grab active:cursor-grabbing shadow-lg hover:bg-purple-700 transition-colors touch-none">
          <GripVertical size={16} />
        </div>
      )}
      <button
        onClick={() => !isEditing && onNavigate(card.page)}
        disabled={isEditing}
        className={`bg-white dark:bg-gray-800 p-4 md:p-6 lg:p-8 rounded-xl shadow-lg transition-all text-left w-full h-full flex flex-col ${isEditing ? 'ring-2 ring-purple-400 ring-dashed cursor-default' : 'hover:shadow-xl transform hover:-translate-y-1'}`}
      >
        <div className={`p-2 md:p-3 rounded-lg ${navColorClasses[card.color]} w-fit mb-2 md:mb-4`}>
          <Icon className="w-6 h-6 md:w-8 md:h-8" />
        </div>
        <h2 className="text-base md:text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1 md:mb-2 leading-tight">{card.title}</h2>
        <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-300 line-clamp-2">{card.description}</p>
      </button>
    </div>
  );
};

export const NavigationWidget = ({ onNavigate, isAdmin = true, isEditing = false }) => { 
  const [cardOrder, setCardOrder] = useState(DEFAULT_NAV_ORDER);
  const [isInitialized, setIsInitialized] = useState(false);

    // Listen for reset
  useEffect(() => {
    const handleReset = () => {
      setCardOrder(DEFAULT_NAV_ORDER);
      localStorage.removeItem('navCardOrder');
    };
    
    window.addEventListener('resetNavCards', handleReset);
    return () => window.removeEventListener('resetNavCards', handleReset);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('navCardOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const validOrder = parsed.filter(id => DEFAULT_NAV_ORDER.includes(id));
        const newCards = DEFAULT_NAV_ORDER.filter(id => !validOrder.includes(id));
        setCardOrder([...validOrder, ...newCards]);
      } catch (e) {
        setCardOrder(DEFAULT_NAV_ORDER);
      }
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('navCardOrder', JSON.stringify(cardOrder));
    }
  }, [cardOrder, isInitialized]);

  const visibleCards = useMemo(() => {
    return cardOrder.filter(id => {
      const card = NAV_CARDS_CONFIG[id];
      return card && (!card.adminOnly || isAdmin);
    }).map(id => NAV_CARDS_CONFIG[id]);
  }, [cardOrder, isAdmin]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      if (navigator.vibrate) navigator.vibrate(25);
    }
  };

  if (!isInitialized) {
    return (
      <div id="navigation" className="bg-transparent">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-xl h-32 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div id="navigation" className="bg-transparent">
      {isEditing ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleCards.map(c => c.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
              {visibleCards.map((card) => (
                <SortableNavCard key={card.id} id={card.id} card={card} onNavigate={onNavigate} isEditing={isEditing} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
          {visibleCards.map((card) => {
            const Icon = card.icon;
            return (
              <button key={card.id} onClick={() => onNavigate(card.page)} className="bg-white dark:bg-gray-800 p-4 md:p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 text-left w-full h-full flex flex-col">
                <div className={`p-2 md:p-3 rounded-lg ${navColorClasses[card.color]} w-fit mb-2 md:mb-4`}>
                  <Icon className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <h2 className="text-base md:text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1 md:mb-2 leading-tight">{card.title}</h2>
                <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-300 line-clamp-2">{card.description}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =====================================================
// CALENDAR HELPERS
// =====================================================
const PERSONAL_CATEGORY_CONFIG = {
  travel: { color: 'bg-blue-500', textColor: 'text-blue-800 dark:text-blue-200', bgLight: 'bg-blue-100 dark:bg-blue-900/30', label: 'Travel' },
  dayoff: { color: 'bg-green-500', textColor: 'text-green-800 dark:text-green-200', bgLight: 'bg-green-100 dark:bg-green-900/30', label: 'Day Off' },
  home: { color: 'bg-purple-500', textColor: 'text-purple-800 dark:text-purple-200', bgLight: 'bg-purple-100 dark:bg-purple-900/30', label: 'Home' },
  other: { color: 'bg-gray-500', textColor: 'text-gray-800 dark:text-gray-200', bgLight: 'bg-gray-100 dark:bg-gray-800', label: 'Other' },
};

const EVENT_COLOR_MAP = {
  orange: { bg: 'bg-orange-500', bgLight: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-200' },
  purple: { bg: 'bg-purple-500', bgLight: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-200' },
  green: { bg: 'bg-green-500', bgLight: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-200' },
  pink: { bg: 'bg-pink-500', bgLight: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-200' },
  cyan: { bg: 'bg-cyan-500', bgLight: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-200' },
  yellow: { bg: 'bg-yellow-500', bgLight: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-200' },
  red: { bg: 'bg-red-500', bgLight: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-200' },
  indigo: { bg: 'bg-indigo-500', bgLight: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-200' },
};

const getShowStatusColors = (show, dateKey) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const showDate = new Date(dateKey);
  showDate.setHours(0, 0, 0, 0);
  const isPast = showDate < today;
  
  if (show.eventType === 'personal' || show.eventType === 'other') {
    if (isPast) {
      return {
        bg: 'bg-gray-100 dark:bg-gray-800',
        text: 'text-gray-500 dark:text-gray-400',
        bgSolid: 'bg-gray-300 dark:bg-gray-600',
        isPast: true,
        isPersonal: true,
        label: show.title || 'Other'
      };
    }
    
    if (show.eventColor && EVENT_COLOR_MAP[show.eventColor]) {
      const colorConfig = EVENT_COLOR_MAP[show.eventColor];
      return {
        bg: colorConfig.bgLight,
        text: colorConfig.text,
        bgSolid: colorConfig.bg,
        isPast: false,
        isPersonal: true,
        label: show.title || 'Other'
      };
    }
    
    const category = show.personalCategory || 'other';
    const config = PERSONAL_CATEGORY_CONFIG[category];
    return {
      bg: config.bgLight,
      text: config.textColor,
      bgSolid: config.color,
      isPast: false,
      isPersonal: true,
      label: config.label
    };
  }
  
  if (isPast) {
    return { 
      bg: 'bg-gray-100 dark:bg-gray-800', 
      text: 'text-gray-500 dark:text-gray-400', 
      bgSolid: 'bg-gray-300 dark:bg-gray-600',
      isPast: true,
      isPersonal: false
    };
  }
  
  const status = show.status || 'confirmed';
  switch (status) {
    case 'tentative': return { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-200', bgSolid: 'bg-yellow-400 dark:bg-yellow-600', isPast: false, isPersonal: false };
    case 'confirmed': return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-200', bgSolid: 'bg-blue-400 dark:bg-blue-600', isPast: false, isPersonal: false };
    case 'completed': return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-200', bgSolid: 'bg-green-400 dark:bg-green-600', isPast: false, isPersonal: false };
    default: return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-200', bgSolid: 'bg-gray-300 dark:bg-gray-600', isPast: false, isPersonal: false };
  }
};

// =====================================================
// MINI CALENDAR
// =====================================================
const MiniCalendar = ({ upcomingShows, onNavigate, shows = {} }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  const getDateKey = (y, m, d) => {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };
  
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());
  
  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  
  const isPast = (day) => {
    const checkDate = new Date(year, month, day);
    checkDate.setHours(0, 0, 0, 0);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return checkDate < todayStart;
  };
  
  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) calendarDays.push(null);
  for (let day = 1; day <= daysInMonth; day++) calendarDays.push(day);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
        <button onClick={goToToday} className="font-semibold text-gray-800 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
          {monthNames[month]} {year}
        </button>
        <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>
      
      <div className="flex items-center justify-center gap-2 text-[10px] mb-1 flex-wrap">
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-yellow-400"></div><span className="text-gray-500 dark:text-gray-400">Tentative</span></div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-blue-400"></div><span className="text-gray-500 dark:text-gray-400">Confirmed</span></div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-purple-400"></div><span className="text-gray-500 dark:text-gray-400">Personal</span></div>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">{day.charAt(0)}</div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => {
          if (day === null) return <div key={`empty-${index}`} className="min-h-[70px]" />;
          
          const dateKey = getDateKey(year, month, day);
          const dayShows = shows[dateKey] || [];
          const hasShows = dayShows.length > 0;
          const dayIsPast = isPast(day);
          const dayIsToday = isToday(day);
          
          return (
            <button
              key={day}
              onClick={() => onNavigate('calendar')}
              className={`min-h-[70px] p-1 rounded-lg flex flex-col transition-all border
                ${dayIsToday ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20' 
                  : hasShows ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20'
                  : 'border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              <span className={`text-xs font-semibold mb-0.5 ${dayIsToday ? 'text-purple-600 dark:text-purple-400' : dayIsPast ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}>{day}</span>
              {hasShows && (
                <div className="flex-1 space-y-0.5 overflow-hidden w-full">
                  {dayShows.slice(0, 2).map(show => {
                    const colors = getShowStatusColors(show, dateKey);
                    return (
                      <div key={show.id} className={`text-[8px] px-1 py-0.5 rounded truncate ${colors.bgSolid} text-white font-medium`} title={show.venue || show.title}>
                        {show.venue || show.title}
                      </div>
                    );
                  })}
                  {dayShows.length > 2 && <p className="text-[8px] text-gray-500 dark:text-gray-400 text-center">+{dayShows.length - 2}</p>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =====================================================
// UPCOMING SHOWS WIDGET
// =====================================================
export const UpcomingShowsWidget = ({ upcomingShows, onNavigate, shows = {} }) => {
  const [viewMode, setViewMode] = useState('list');
  const [isExpanded, setIsExpanded] = useState(false);
  
  const showsOnly = upcomingShows.filter(s => s.eventType !== 'other');
  const DEFAULT_VISIBLE = 3;
  const visibleShows = isExpanded ? showsOnly : showsOnly.slice(0, DEFAULT_VISIBLE);
  const hasMoreShows = showsOnly.length > DEFAULT_VISIBLE;
  const remainingCount = showsOnly.length - DEFAULT_VISIBLE;
  
  return (
    <div id="upcoming" className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100">Next Shows</h2>
          {showsOnly.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{showsOnly.length}</span>
          )}
        </div>
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} title="List View">
            <LayoutList size={18} />
          </button>
          <button onClick={() => setViewMode('calendar')} className={`p-1.5 rounded-md transition-all ${viewMode === 'calendar' ? 'bg-white dark:bg-gray-600 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} title="Calendar View">
            <CalendarDays size={18} />
          </button>
        </div>
      </div>
      
      {viewMode === 'list' ? (
        showsOnly.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400 text-center py-8">No upcoming shows scheduled</p>
        ) : (
          <>
            <div className="space-y-3">
              {visibleShows.map(show => (
                <div key={show.id} className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-3 md:p-4 rounded">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="mb-2 md:mb-0">
                      <h3 className="font-bold text-base md:text-lg text-gray-800 dark:text-gray-100">{show.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{show.venue}{show.city && `, ${show.city}`}</p>
                    </div>
                    <div className="text-left md:text-right">
                      <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{formatDate(show.date)}</p>
                      {show.showTime && <p className="text-xs text-gray-600 dark:text-gray-400">Show: {show.showTime}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {hasMoreShows && (
              <button onClick={() => setIsExpanded(!isExpanded)} className="w-full mt-3 py-2.5 flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors font-medium border border-blue-200 dark:border-blue-800">
                {isExpanded ? (<><ChevronUp size={18} />Show Less</>) : (<><ChevronDown size={18} />Show {remainingCount} More Show{remainingCount !== 1 ? 's' : ''}</>)}
              </button>
            )}
            <button onClick={() => onNavigate('timeline')} className="mt-4 w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">View All Shows</button>
          </>
        )
      ) : (
        <>
          <MiniCalendar upcomingShows={upcomingShows} onNavigate={onNavigate} shows={shows} />
          <button onClick={() => onNavigate('calendar')} className="mt-4 w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Open Full Calendar</button>
        </>
      )}
    </div>
  );
};